// @flow
import * as React from 'react';

import { useDispatch } from 'react-redux';

import { getCart, selectCartCurrency, updateProducts } from '@gandi/cart';
import type { DecoratedProduct } from '@gandi/cart';
import { useTranslator } from '@gandi/translate';
import { Base, Text, Switch } from '@gandi/ui';

import { useStandaloneMailboxPrice } from 'src/MailboxStandaloneCreate/modules/hooks/useProductPrice';
import { ListRow } from 'src/Overview/components/ListRow';
import { useAppSelector } from 'src/hooks/useAppSelector';

type UpdateProductsParameters = Parameters<typeof updateProducts>;

type Props =
  | {
      product: DecoratedProduct,
      bundle?: void,
    }
  | { product?: void, bundle: Array<DecoratedProduct> };

export function FreeMailboxRow({
  product,
  bundle,
}: Props): React.MixedElement | null {
  const allProducts = product
    ? [product]
    : (!!bundle?.length && bundle) || [
        { id: 'NONE', payload: { mailbox_optin: false } },
      ];

  const [disabled, setDisabled] = React.useState<boolean>(false);
  const {
    payload: { mailbox_optin: mailboxOptin },
  } = allProducts[0];

  const [value, setValue] = React.useState(!!mailboxOptin);

  const { __, formatPrice } = useTranslator();
  const dispatch = useDispatch();
  const currency = useAppSelector(selectCartCurrency);

  // mailbox price
  const mbPrice = useStandaloneMailboxPrice({});

  const handleOnChange: (evt: SyntheticInputEvent<>) => mixed = evt => {
    setDisabled(true);

    const mailbox_optin = evt.target.checked;

    // XXX: mix of flow tools:
    // - Parameters<F>
    // - and $PropertyType<T, k>
    // does not make easy to read
    const bulkPayload: UpdateProductsParameters[0]['productsData'] = [];

    allProducts.forEach(oneProduct => {
      if (oneProduct.id === 'NONE') return;

      const newPayload = { ...oneProduct.payload, mailbox_optin };
      bulkPayload.push({
        id: oneProduct.id,
        process: oneProduct.process,
        product_type: oneProduct.productType,
        payload: newPayload,
      });
    });

    if (bulkPayload.length === 0) return;

    setValue(mailbox_optin);
    dispatch(updateProducts({ productsData: bulkPayload }))
      // To update the contracts
      .then(() => dispatch(getCart()))
      .catch(() => setValue(!mailbox_optin))
      .finally(() => setDisabled(false));
  };

  const switchName = `free-mailbox-${allProducts[0].id}`;

  return (
    <ListRow
      optionsArrow={false}
      title={
        value
          ? __('2 mailboxes included for 1 year free of charge')
          : __('Try 2 mailboxes included for 1 year free of charge')
      }
      cta={
        <Base flex flexAlignItemsCenter gap="small">
          <Text color="weak">
            {value ? __('Free trial') : __('Activate for free')}
          </Text>
          <Switch
            name={switchName}
            checked={value}
            onChange={handleOnChange}
            variant="compact"
            disabled={disabled}
          >
            {__('Try our mailbox offer')}
          </Switch>
        </Base>
      }
      intent={value ? 'success' : 'off'}
      options={[
        __(
          'Then, %(mbPrice)s/mo (excl. VAT) per mailbox. Commitment-free offer. Cancel anytime.',
          {
            mbPrice: mbPrice
              ? formatPrice(mbPrice, currency)
              : __('Price unavailable'),
          },
        ),
      ]}
    />
  );
}
